// ── Build a real OpsCenterMockup Task[] tree from accepted AI Configure
// suggestions ─────────────────────────────────────────────────────────────
//
// This is the mechanism behind "the workflow builder should represent what
// was accepted during account setup": rather than a static chip-strip, it
// constructs the same StepTask/BranchTask shapes the real Ops Center canvas
// understands (see OpsCenterMockup/types.ts + graph.ts), so accepted
// suggestions render as an actual workflow graph via the shared nodeTypes
// registry.
//
// Worked example (Campaign Agent): the customer runs two accounts — an
// existing safe-harbour eKYC recipe handling ~90% of onboarding requests
// (shown separately via V1RecipeVisual, unaffected by this file), and a
// second account that runs a Fraud Signal Check against every onboarding
// request and only steps up to Biometrics/IDV when that check finds risk.
// That's a genuine conditional branch, not a straight-through pipeline —
// when both `fraud` and `idv` are accepted together, this builder gates the
// IDV step inside a BRANCH off the fraud step's result instead of chaining
// them as two sequential steps. Any other combination of accepted stages
// still renders as a straight-through sequence.
import type { Task, StepTask, BranchTask } from '@features/dashboards/components/OpsCenterMockup/types'
import type { AiConfigureSuggestions } from './aiConfigure'

export interface AcceptedWorkflowStages {
  duplicates: boolean
  cascade: boolean
  idv: boolean
  aml: boolean
  kyb: boolean
  fraud: boolean
  tm: boolean
}

export const NO_ACCEPTED_WORKFLOW_STAGES: AcceptedWorkflowStages = {
  duplicates: false, cascade: false, idv: false, aml: false, kyb: false, fraud: false, tm: false,
}

export function hasAnyAcceptedWorkflowStage(accepted: AcceptedWorkflowStages): boolean {
  return Object.values(accepted).some(Boolean)
}

function step(id: string, name: string, stepName: StepTask['configuration']['name'], description?: string): StepTask {
  return { id, name, description, configuration: { type: 'STEP', name: stepName } }
}

/**
 * Builds the Task[] tree for whatever stages the rep has accepted so far.
 * `suggestions` is only used for rationale text on the Fraud/IDV steps (the
 * gated pair) — every other step just gets a plain, descriptive label.
 */
export function buildWorkflowTasksFromAcceptedStages(
  accepted: AcceptedWorkflowStages,
  suggestions?: AiConfigureSuggestions | null,
): Task[] {
  const tasks: Task[] = []

  if (accepted.duplicates) tasks.push(step('step_matchlist', 'Duplicate / Matchlist Check', 'MATCHLIST'))
  if (accepted.cascade) tasks.push(step('step_kyc', 'KYC Cascade', 'KYC'))

  const fraudGatesIdv = accepted.fraud && accepted.idv

  if (fraudGatesIdv) {
    const fraudStep = step(
      'step_fraud_signals',
      'Fraud Signal Check',
      'FRAUD',
      suggestions?.fraud?.rationale ?? 'Runs device, IP, email and phone risk signals on every onboarding request.',
    )
    tasks.push(fraudStep)

    const idvStep = step(
      'step_idv_biometrics',
      'IDV & Biometrics Step-Up',
      'IDV',
      suggestions?.idv?.rationale ?? 'Document + biometric verification, triggered only when fraud risk is detected.',
    )
    const branch: BranchTask = {
      id: 'branch_fraud_risk_gate',
      name: 'Fraud risk detected?',
      description: 'Steps up to biometrics only for requests the fraud check flags as risky — the other ~90% pass straight through.',
      configuration: {
        type: 'BRANCH',
        branches: [
          {
            name: 'Fraud risk detected',
            expression: { type: 'SIMPLE', sourcePath: `tasks.${fraudStep.id}.stepResult.riskLevel`, operator: 'NEQ', value: 'LOW' },
            tasks: [idvStep],
          },
          {
            name: 'No fraud signal',
            tasks: [],
          },
        ],
      },
    }
    tasks.push(branch)
  } else {
    if (accepted.idv) tasks.push(step('step_idv', 'IDV & Biometrics', 'IDV', suggestions?.idv?.rationale))
    if (accepted.fraud) tasks.push(step('step_fraud', 'Fraud Signal Check', 'FRAUD', suggestions?.fraud?.rationale))
  }

  if (accepted.aml) tasks.push(step('step_aml', 'AML Screening', 'AML'))
  if (accepted.kyb) tasks.push(step('step_kyb', 'KYB Checks', 'KYB'))
  if (accepted.tm) tasks.push(step('step_tm', 'Transaction Monitoring', 'TOGGLEMONITORING'))

  return tasks
}
